import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { 
  uploadPDF, 
  getPDF, 
  getPDFPage,
  deletePDF, 
  listPDFs,
  getLayerDocument,
  listLayers,
  listLayerHistory,
  createLayer,
  updateLayer,
  deleteLayer,
  listPunchItems,
  listObservations,
  listSubmittals,
  listRFIs,
  getPDFRevisions,
  getPDFReferences,
  publishSheetRevision,
  matchPDFPageToSheet,
  listPDFPages,
  updatePDFPage,
  listProjectDrawingSheets,
  getPDFActivity,
  patchPDFPageMetadata,
  PDFFile,
  PDFFileList,
  LayerDocument,
  Layer,
  PunchListItemRef,
  ObservationRef,
  SubmittalRef,
  RFIRef,
  PDFRevisionOption,
  SheetRevisionDetail,
  PDFPage,
  DrawingSheetOption,
  DrawingReferenceLink,
  DrawingActivityItem,
} from '../api';
import {
  enqueueLayerMutation,
  listLayerMutations,
  makeMutationId,
  removeLayerMutation,
} from '../services/offlineStore';

// Query keys for cache management
export const PDF_QUERY_KEYS = {
  all: ['pdf-editor'] as const,
  pdfs: () => [...PDF_QUERY_KEYS.all, 'pdfs'] as const,
  pdf: (id: string) => [...PDF_QUERY_KEYS.all, 'pdf', id] as const,
  pdfPage: (id: string) => [...PDF_QUERY_KEYS.all, 'pdf-page', id] as const,
  pdfsByProject: (projectId: number) => [...PDF_QUERY_KEYS.all, 'pdfs', 'project', projectId] as const,
  layerDocument: (pdfId: string) => [...PDF_QUERY_KEYS.all, 'layer-document', pdfId] as const,
  layers: (layerDocumentId: string, pageNumber?: number, markupScope: 'all' | 'personal' | 'shared' = 'all') => (
    [...PDF_QUERY_KEYS.all, 'layers', layerDocumentId, pageNumber ?? 'all', markupScope] as const
  ),
  layerHistory: (layerId: string) => [...PDF_QUERY_KEYS.all, 'layer-history', layerId] as const,
  punchItems: (projectId: number, search: string) => [...PDF_QUERY_KEYS.all, 'punch-items', projectId, search] as const,
  observations: (projectId: number, search: string) => [...PDF_QUERY_KEYS.all, 'observations', projectId, search] as const,
  submittals: (projectId: number, search: string) => [...PDF_QUERY_KEYS.all, 'submittals', projectId, search] as const,
  rfis: (projectId: number, search: string) => [...PDF_QUERY_KEYS.all, 'rfis', projectId, search] as const,
  pdfRevisions: (pdfId: string, pageNumber?: number) => [...PDF_QUERY_KEYS.all, 'pdf-revisions', pdfId, pageNumber ?? 'none'] as const,
  pdfPages: (pdfId: string, pageNumber?: number, needsSheetReview?: boolean) => (
    [...PDF_QUERY_KEYS.all, 'pdf-pages', pdfId, pageNumber ?? 'all', needsSheetReview ? 'review' : 'all'] as const
  ),
  drawingSheets: (pdfId: string, search: string) => [...PDF_QUERY_KEYS.all, 'drawing-sheets', pdfId, search] as const,
  pdfReferences: (pdfId: string, pageNumber?: number) => [...PDF_QUERY_KEYS.all, 'pdf-references', pdfId, pageNumber ?? 'none'] as const,
  pdfActivity: (pdfId: string, limit: number) => [...PDF_QUERY_KEYS.all, 'pdf-activity', pdfId, limit] as const,
};

/**
 * Hook for uploading a PDF file
 */
export function useUploadPDF() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, projectId }: { file: File; projectId: number }) => 
      uploadPDF(file, projectId),
    onSuccess: (data) => {
      // Invalidate PDFs list to refetch
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdfs() });
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdfsByProject(data.project) });
      // Set the new PDF in cache
      queryClient.setQueryData(PDF_QUERY_KEYS.pdf(data.id), data);
    },
  });
}

/**
 * Hook for fetching a specific PDF file
 */
export function useGetPDF(pdfId: string | null) {
  return useQuery<PDFFile, Error>({
    queryKey: PDF_QUERY_KEYS.pdf(pdfId ?? ''),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return getPDF(pdfId);
    },
    enabled: Boolean(pdfId),
    staleTime: 60_000, // 1 minute
  });
}

export function useGetPDFPage(pageId: string | null) {
  return useQuery<PDFPage, Error>({
    queryKey: PDF_QUERY_KEYS.pdfPage(pageId ?? ''),
    queryFn: () => {
      if (!pageId) throw new Error('PDF page ID is required');
      return getPDFPage(pageId);
    },
    enabled: Boolean(pageId),
    staleTime: 30_000,
  });
}

/**
 * Hook for listing all PDFs (optionally filtered by project)
 */
export function useListPDFs(projectId?: number) {
  return useQuery<PDFFileList[], Error>({
    queryKey: projectId ? PDF_QUERY_KEYS.pdfsByProject(projectId) : PDF_QUERY_KEYS.pdfs(),
    queryFn: () => listPDFs(projectId),
    staleTime: 30_000, // 30 seconds
  });
}

export function useUpdatePDFPageMetadata(projectId: number) {
  const queryClient = useQueryClient();
  const listKey = PDF_QUERY_KEYS.pdfsByProject(projectId);

  return useMutation({
    mutationFn: ({
      pageId,
      updates,
    }: {
      pdfId: string;
      pageId: string;
      updates: Partial<Pick<PDFPage, 'drawing_number' | 'title' | 'discipline'>>;
    }) => patchPDFPageMetadata(pageId, updates),
    onMutate: async ({ pdfId, updates }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<PDFFileList[]>(listKey);
      queryClient.setQueryData<PDFFileList[]>(listKey, (current = []) => current.map((pdf) => {
        if (pdf.id !== pdfId) return pdf;
        return {
          ...pdf,
          ...(updates.drawing_number !== undefined
            ? { primary_drawing_number: updates.drawing_number }
            : {}),
          ...(updates.title !== undefined ? { primary_title: updates.title } : {}),
          ...(updates.discipline !== undefined ? { disciplines: [updates.discipline].filter(Boolean) } : {}),
        };
      }));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdf(variables.pdfId) });
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdfPage(variables.pageId) });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf-pages', variables.pdfId] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'drawing-sheets'] });
    },
  });
}

/**
 * Hook for deleting a PDF file
 */
export function useDeletePDF() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pdfId: string) => deletePDF(pdfId),
    onSuccess: (_, pdfId) => {
      // Remove the PDF from cache
      queryClient.removeQueries({ queryKey: PDF_QUERY_KEYS.pdf(pdfId) });
      // Invalidate PDFs list
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdfs() });
    },
  });
}

/**
 * Hook for getting the layer document for a PDF
 */
export function useLayerDocument(pdfId: string | null) {
  return useQuery<LayerDocument, Error>({
    queryKey: PDF_QUERY_KEYS.layerDocument(pdfId ?? ''),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return getLayerDocument(pdfId);
    },
    enabled: Boolean(pdfId),
    staleTime: 60_000, // 1 minute
  });
}

export function useListLayers(
  layerDocumentId: string | null,
  pageNumber?: number,
  markupScope: 'all' | 'personal' | 'shared' = 'all'
) {
  return useQuery<Layer[], Error>({
    queryKey: PDF_QUERY_KEYS.layers(layerDocumentId ?? '', pageNumber, markupScope),
    queryFn: () => {
      if (!layerDocumentId) throw new Error('Layer document ID is required');
      return listLayers(layerDocumentId, pageNumber, markupScope);
    },
    enabled: Boolean(layerDocumentId),
    staleTime: 10_000,
  });
}

export function useLayerHistory(layerId: string | null) {
  return useQuery({
    queryKey: PDF_QUERY_KEYS.layerHistory(layerId ?? ''),
    queryFn: () => {
      if (!layerId) throw new Error('Layer ID is required');
      return listLayerHistory(layerId);
    },
    enabled: Boolean(layerId),
    staleTime: 5_000,
  });
}

export function usePunchItems(projectId: number | null, search = '') {
  return useQuery<PunchListItemRef[], Error>({
    queryKey: PDF_QUERY_KEYS.punchItems(projectId ?? 0, search),
    queryFn: () => {
      if (!projectId) throw new Error('Project ID is required');
      return listPunchItems(projectId, search);
    },
    enabled: Boolean(projectId),
    staleTime: 15_000,
  });
}

export function useObservations(projectId: number | null, search = '') {
  return useQuery<ObservationRef[], Error>({
    queryKey: PDF_QUERY_KEYS.observations(projectId ?? 0, search),
    queryFn: () => {
      if (!projectId) throw new Error('Project ID is required');
      return listObservations(projectId, search);
    },
    enabled: Boolean(projectId),
    staleTime: 15_000,
  });
}

export function useDrawingSubmittals(projectId: number | null, search = '') {
  return useQuery<SubmittalRef[], Error>({
    queryKey: PDF_QUERY_KEYS.submittals(projectId ?? 0, search),
    queryFn: () => {
      if (!projectId) throw new Error('Project ID is required');
      return listSubmittals(projectId, search);
    },
    enabled: Boolean(projectId),
    staleTime: 15_000,
  });
}

export function useDrawingRFIs(projectId: number | null, search = '') {
  return useQuery<RFIRef[], Error>({
    queryKey: PDF_QUERY_KEYS.rfis(projectId ?? 0, search),
    queryFn: () => {
      if (!projectId) throw new Error('Project ID is required');
      return listRFIs(projectId, search);
    },
    enabled: Boolean(projectId),
    staleTime: 15_000,
  });
}

export function usePDFRevisions(pdfId: string | null, pageNumber?: number) {
  return useQuery<{
    pdf_file: string;
    page_number?: number;
    sheet_id?: string;
    sheet_identity_key?: string;
    drawing_number?: string;
    title?: string;
    discipline?: string;
    group_key?: string;
    count: number;
    results: PDFRevisionOption[];
  }, Error>({
    queryKey: PDF_QUERY_KEYS.pdfRevisions(pdfId ?? '', pageNumber),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return getPDFRevisions(pdfId, pageNumber);
    },
    enabled: Boolean(pdfId),
    staleTime: 30_000,
  });
}

export function usePDFReferences(pdfId: string | null, pageNumber?: number) {
  return useQuery<{
    pdf_file: string;
    page_number: number;
    sheet_id?: string;
    drawing_number?: string;
    title?: string;
    reference_numbers_found: string[];
    count: number;
    results: DrawingReferenceLink[];
  }, Error>({
    queryKey: PDF_QUERY_KEYS.pdfReferences(pdfId ?? '', pageNumber),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return getPDFReferences(pdfId, pageNumber);
    },
    enabled: Boolean(pdfId),
    staleTime: 30_000,
  });
}

export function usePDFPages(pdfId: string | null, pageNumber?: number, needsSheetReview?: boolean) {
  return useQuery<PDFPage[], Error>({
    queryKey: PDF_QUERY_KEYS.pdfPages(pdfId ?? '', pageNumber, needsSheetReview),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return listPDFPages(pdfId, pageNumber, needsSheetReview);
    },
    enabled: Boolean(pdfId),
    staleTime: 15_000,
  });
}

export function usePDFActivity(pdfId: string | null, limit = 50) {
  return useQuery<{ pdf_file: string; count: number; results: DrawingActivityItem[] }, Error>({
    queryKey: PDF_QUERY_KEYS.pdfActivity(pdfId ?? '', limit),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return getPDFActivity(pdfId, limit);
    },
    enabled: Boolean(pdfId),
    staleTime: 5_000,
  });
}

export function useProjectDrawingSheets(pdfId: string | null, search = '') {
  return useQuery<{ pdf_file: string; count: number; results: DrawingSheetOption[] }, Error>({
    queryKey: PDF_QUERY_KEYS.drawingSheets(pdfId ?? '', search),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return listProjectDrawingSheets(pdfId, search);
    },
    enabled: Boolean(pdfId),
    staleTime: 30_000,
  });
}

export function usePublishSheetRevision(pdfId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sheetRevisionId: string) => {
      if (!pdfId) throw new Error('PDF ID is required');
      return publishSheetRevision(pdfId, sheetRevisionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf-revisions'] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf'] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf-pages'] });
    },
  });
}

export function useMatchPDFPageToSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pageId,
      payload,
    }: {
      pageId: string;
      payload: {
        sheet_id?: string;
        create_new_sheet?: boolean;
        drawing_number?: string;
        title?: string;
        discipline?: string;
        revision_number?: number;
        revision_label?: string;
        set_current?: boolean;
      };
    }): Promise<{ status: string; page: PDFPage; revision: SheetRevisionDetail }> => (
      matchPDFPageToSheet(pageId, payload)
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf-revisions'] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf'] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf-pages'] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'drawing-sheets'] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layers'] });
    },
  });
}

export function useUpdatePDFPageRotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pageId,
      rotation,
    }: {
      pageId: string;
      rotation: number;
    }): Promise<PDFPage> => (
      updatePDFPage(pageId, { rotation })
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf-pages'] });
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf'] });
    },
  });
}

type QueuedMutationOperation = 'create' | 'update' | 'delete';

const useLayerOutboxSync = () => {
  const queryClient = useQueryClient();

  const flushOutbox = useCallback(async () => {
    if (!navigator.onLine) return;
    const queue = await listLayerMutations();
    const sorted = [...queue].sort((a, b) => a.createdAt - b.createdAt);

    for (const queued of sorted) {
      try {
        if (queued.operation === 'create') {
          await createLayer(queued.payload as any, {
            client_mutation_id: queued.id,
          });
        } else if (queued.operation === 'update' && queued.layerId) {
          await updateLayer(queued.layerId, queued.payload as any, {
            client_mutation_id: queued.id,
          });
        } else if (queued.operation === 'delete' && queued.layerId) {
          await deleteLayer(queued.layerId, {
            client_mutation_id: queued.id,
          });
        }

        await removeLayerMutation(queued.id);
      } catch (error) {
        // Keep failed mutations in queue for future retry.
        console.error('Failed to flush layer outbox item', queued.id, error);
      }
    }

    queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layers'] });
    queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layer-history'] });
    queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layer-document'] });
  }, [queryClient]);

  useEffect(() => {
    const handleOnline = () => {
      void flushOutbox();
    };
    window.addEventListener('online', handleOnline);
    void flushOutbox();

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [flushOutbox]);

  return { flushOutbox };
};

const queueLayerMutation = async (
  operation: QueuedMutationOperation,
  layerDocumentId: string,
  payload: Record<string, unknown>,
  layerId?: string
) => {
  const mutationId = makeMutationId();
  await enqueueLayerMutation({
    id: mutationId,
    createdAt: Date.now(),
    operation,
    layerId,
    layerDocumentId,
    payload,
  });
};

export function useLayerMutations() {
  const queryClient = useQueryClient();
  const { flushOutbox } = useLayerOutboxSync();

  const invalidateLayerCaches = useCallback((layerDocumentId?: string, layerId?: string) => {
    if (layerDocumentId) {
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layers', layerDocumentId] });
    } else {
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layers'] });
    }
    if (layerId) {
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.layerHistory(layerId) });
    } else {
      queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layer-history'] });
    }
    queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'pdf-activity'] });
    queryClient.invalidateQueries({ queryKey: [...PDF_QUERY_KEYS.all, 'layer-document'] });
  }, [queryClient]);

  const createLayerMutation = useMutation({
    mutationFn: async (payload: Omit<Layer, 'id' | 'created_at' | 'updated_at' | 'created_by'>) => {
      if (!navigator.onLine) {
        await queueLayerMutation('create', payload.layer_document, payload as any);
        return null;
      }
      try {
        return await createLayer(payload);
      } catch (error) {
        await queueLayerMutation('create', payload.layer_document, payload as any);
        return null;
      }
    },
    onSettled: (_data, _error, variables) => {
      invalidateLayerCaches(variables.layer_document);
    },
  });

  const updateLayerMutation = useMutation({
    mutationFn: async ({
      layerId,
      layerDocumentId,
      updates,
    }: {
      layerId: string;
      layerDocumentId: string;
      updates: Partial<Pick<Layer, 'data' | 'z_index' | 'visible' | 'markup_scope' | 'punch_list_item' | 'observation' | 'submittal' | 'rfi'>>;
    }) => {
      if (!navigator.onLine) {
        await queueLayerMutation('update', layerDocumentId, updates as any, layerId);
        return null;
      }
      try {
        return await updateLayer(layerId, updates);
      } catch {
        await queueLayerMutation('update', layerDocumentId, updates as any, layerId);
        return null;
      }
    },
    onSettled: (_data, _error, variables) => {
      invalidateLayerCaches(variables.layerDocumentId, variables.layerId);
    },
  });

  const deleteLayerMutation = useMutation({
    mutationFn: async ({
      layerId,
      layerDocumentId,
    }: {
      layerId: string;
      layerDocumentId: string;
    }) => {
      if (!navigator.onLine) {
        await queueLayerMutation('delete', layerDocumentId, {}, layerId);
        return null;
      }
      try {
        await deleteLayer(layerId);
      } catch {
        await queueLayerMutation('delete', layerDocumentId, {}, layerId);
      }
      return null;
    },
    onSettled: (_data, _error, variables) => {
      invalidateLayerCaches(variables.layerDocumentId, variables.layerId);
    },
  });

  return {
    createLayerMutation,
    updateLayerMutation,
    deleteLayerMutation,
    flushOutbox,
  };
}
